import React from 'react';
import { Breadcrumb } from 'antd';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type MenuItem } from '@/api/services/menu.service';
import { useMenu } from '@/context/MenuContext';
import { ChatPageHeader } from '@/pages/Chat/ChatPageHeader';
import './MenuBreadcrumb.css';

export const MenuBreadcrumb: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { menuItems } = useMenu();

  // Find menu item by URL and return the full path from root
  const findMenuItemWithPath = (
    items: MenuItem[],
    targetUrl: string,
    currentPath: Array<{ item: MenuItem; url: string }> = []
  ): Array<{ item: MenuItem; url: string }> | null => {
    for (const item of items) {
      const newPath = [...currentPath, { item, url: item.url || '' }];

      if (item.url === targetUrl) {
        return newPath;
      }

      if (item.children) {
        const found = findMenuItemWithPath(item.children, targetUrl, newPath);
        if (found) return found;
      }
    }
    return null;
  };

  // Build breadcrumb path
  const buildBreadcrumbPath = () => {
    const currentUrl = location.pathname;
    const menuPath = findMenuItemWithPath(menuItems, currentUrl);

    if (!menuPath) {
      return [];
    }

    // Filter out items without URL (section headers) and build breadcrumb
    return menuPath
      .filter(({ url }) => url && url !== '')
      .map(({ item, url }) => ({
        title: t(`menu.${item.code}`, item.code),
        path: url,
      }));
  };

  const breadcrumbPath = buildBreadcrumbPath();

  // Special case: Chat page uses custom header instead of breadcrumb
  if (location.pathname === '/apps/chat') {
    return <ChatPageHeader />;
  }

  if (breadcrumbPath.length === 0) {
    return null;
  }

  return (
    <Breadcrumb
      className="menu-breadcrumb"
      separator="::"
      items={breadcrumbPath.map((item, index) => ({
        title:
          index < breadcrumbPath.length - 1 ? (
            <Link to={item.path}>{item.title}</Link>
          ) : (
            item.title
          ),
      }))}
    />
  );
};
